import { batch, useDispatch } from 'react-redux';
import { setError, setIsLoading, setStore } from 'pages/store/actions/employeeShops';
import { getEmployeeID, getIsValidEmployeeID, getLoggedInEmployeeID } from '../utils/employee';

import { fetchAndGetEmployeeShopIDs } from '../utils/employeeShops';
import { fetchShops } from '../utils/shops';
import trackError from '../utils/trackError';

interface UseEmployeeShopsInput {
  isPrimaryUser: boolean;
}

const useEmployeeShops = ({ isPrimaryUser = false }: UseEmployeeShopsInput) => {
  const dispatch = useDispatch();

  const fetchEmployeeShops = async () => {
    const employeeID = getEmployeeID();
    const loggedInEmployeeID = getLoggedInEmployeeID();
    const isValidEmployeeID = getIsValidEmployeeID(employeeID);
    const isLoggedIn = employeeID === loggedInEmployeeID;

    // Skips API calls for new employees
    if (!isValidEmployeeID) {
      return;
    }

    try {
      const [shopsResponse, loggedInEmployeeShopIDsResponse] = await Promise.all([
        fetchShops(),
        fetchAndGetEmployeeShopIDs({
          employeeID: loggedInEmployeeID,
        }),
      ]);

      // Skips API call if the employee is logged in
      const employeeShopIDsResponse = isLoggedIn
        ? [...loggedInEmployeeShopIDsResponse]
        : await fetchAndGetEmployeeShopIDs({ employeeID });

      // CLD-33565: Shops the logged in user can assign to other employees
      const allowedShopsArray = shopsResponse.filter(shop =>
        loggedInEmployeeShopIDsResponse.includes(shop.id)
      );

      // Employee shops from the allowed shops
      const employeeShopsArray = allowedShopsArray.map(shop => {
        const hasAccess = employeeShopIDsResponse.includes(shop.id);
        return {
          ...shop,
          hasAccess,
          isDefault: hasAccess,
        };
      });

      const hasFullAccess = loggedInEmployeeShopIDsResponse.length === shopsResponse.length;

      dispatch(
        setStore({
          // CLD-33565: primary users and employees without full access cannot manage access
          canChangeFullAccess: !isPrimaryUser && hasFullAccess,
          employeeID,
          error: undefined,
          isLoading: false,
          isPrimaryUser,
          shops: employeeShopsArray,
        })
      );
    } catch (error) {
      const errorTrace = {
        description: 'Error: The system could not fetch any shops.',
        error,
        flashNotifier: true,
      };

      batch(() => {
        dispatch(setError(errorTrace));
        dispatch(setIsLoading(false));
      });

      trackError(errorTrace);
    }
  };

  return {
    fetchEmployeeShops,
  };
};

export default useEmployeeShops;
